import { useEffect } from "react";
import { categorias } from "@/mocks/products";
import type { Produto } from "@/mocks/products";

interface ProductModalProps {
  produto: Produto | null;
  onClose: () => void;
}

export default function ProductModal({ produto, onClose }: ProductModalProps) {
  useEffect(() => {
    if (!produto) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [produto, onClose]);

  const categoria = produto ? categorias.find((c) => c.slug === produto.categoria)?.nome ?? "" : "";

  return (
    <div className={`fixed inset-0 z-[70] ${produto ? "" : "pointer-events-none"}`} aria-hidden={!produto}>
      <div
        className={`absolute inset-0 bg-primary-950/70 backdrop-blur-sm transition-opacity duration-300 ${
          produto ? "opacity-100" : "opacity-0"
        }`}
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-label={produto ? produto.nome : "Detalhe do produto"}
        className={`absolute inset-y-0 right-0 flex w-full max-w-lg flex-col border-l border-background-200 bg-background-50 transition-transform duration-500 ease-editorial ${
          produto ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {produto && (
          <>
            <div className="flex items-center justify-between border-b border-background-200 px-5 py-4 md:px-7 md:py-5">
              <span className="ocean-label text-[10px] text-accent-600">{categoria}</span>
              <button
                type="button"
                onClick={onClose}
                className="flex h-10 w-10 cursor-pointer items-center justify-center text-foreground-600 transition-colors hover:text-accent-600"
                aria-label="Fechar detalhe do produto"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="aspect-[4/3] w-full bg-background-100 md:aspect-[4/3]">
                <img
                  src={produto.imagem}
                  alt={produto.nome}
                  title={`${produto.nome} — Ocean Companhia de Comércio`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <div className="px-5 py-8 md:px-7 md:py-10">
                <span className="block h-px w-12 bg-accent-500" />
                <h3 className="mt-6 font-heading text-3xl font-medium text-foreground-950 md:text-4xl">
                  {produto.nome}
                </h3>

                {produto.descricao ? (
                  <p className="mt-5 text-sm leading-relaxed text-foreground-600">{produto.descricao}</p>
                ) : (
                  <p className="mt-5 text-sm leading-relaxed text-foreground-500">
                    Informações detalhadas sobre este produto estão disponíveis sob consulta comercial.
                  </p>
                )}

                <a
                  href="#contato"
                  onClick={onClose}
                  className="mt-9 inline-flex cursor-pointer items-center gap-2 rounded-md bg-accent-500 px-5 py-3 text-sm font-medium text-primary-950 transition-colors duration-300 hover:bg-accent-400"
                >
                  Consultar informações
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}